import { BrowserRouter } from "react-router-dom";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore } from "redux-persist";
import { store } from "./app/store";
import App from "./App.tsx";
import "./assets/styles/globals.css";
import * as Sentry from "@sentry/react";
import "./helpers/i18n";
import { guardHistoryRateLimit } from "./helpers/historyRateLimitGuard";
import { initializeGTM } from "./helpers/GTMTracking";

/**
 * Type declarations for global variables
 */
declare var ApplePaySession: any;

declare global {
  interface Window {
    dataLayer: any[];
    ApplePaySession: typeof ApplePaySession | undefined;
  }
}

guardHistoryRateLimit();
initializeGTM();

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_URL,
  enabled: import.meta.env.VITE_SENTRY_ENABLED === "true",
  ignoreErrors: ["Network Error"],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    /^https:\/\/booking.cityhub.com/,
    /^http:\/\/booking-test.cityhub.com/,
    /^http:\/\/chapi.cityhub.com/,
    /^http:\/\/chapi-test.cityhub.com/,
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions for performance monitoring. Adjust this value in production as needed.
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

/**
 * Enable persistence for Redux store
 * Save all state to local storage
 */
let persistor = persistStore(store);

/**
 * Render the React application
 * Only if the target div is present
 */
const div = document.querySelector<HTMLElement>("#booking-module");
if (div) {
  const root = createRoot(div);
  root.render(
    <BrowserRouter>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <App />
        </PersistGate>
      </Provider>
    </BrowserRouter>,
  );
}
